import { t } from 'i18next';

import {
  CurrentPlanResponse,
  PendingPlanChange,
  StudioPlan,
  StudioPlanChangeInterval,
  StudioPlanChangePreviewRequest,
  StudioPlanId,
  StudioPlanTier,
} from 'src/services/api/studioCreditApi';

import {
  EMPTY_VALUE,
  getStudioPlanTitle,
  STUDIO_PLAN_RANK,
} from './studioFeatureGating';

export type SellableStudioPlanId = Exclude<StudioPlanId, 'freemium'>;

export type StudioBillingInterval = 'month' | 'year';

// Annual prices are stored per year with the discount baked in; the "starting
// at" figures render as an equivalent per-month price.
export function monthlyPriceCents(
  tier: StudioPlanTier,
  interval: StudioBillingInterval,
): number {
  if (interval === 'year' && tier.annualCents !== undefined) {
    return tier.annualCents / 12;
  }
  return tier.monthlyCents;
}

/**
 * A plan-change target: the plan, credit total, and interval. Grid cards only
 * produce sellable plans; the manage-plan downgrade path can target freemium.
 */
export interface StudioPlanSelection {
  planId: StudioPlanId;
  planTitle: string;
  interval: StudioBillingInterval;
  tier: {
    credits: number;
    monthlyCents: number;
    annualCents?: number;
    /** The target plan's included allowance; anchors the modal's dropdown. */
    includedCredits?: number;
  };
}

/** Maps the page's month/year toggle onto the catalog's canonical intervals. */
export const toApiInterval = (
  interval: StudioBillingInterval,
): StudioPlanChangeInterval => (interval === 'year' ? 'annual' : 'monthly');

/**
 * Maps a subscription's interval string onto the page's month/year toggle;
 * anything other than the annual marker reads as monthly.
 */
export const fromApiInterval = (interval?: string): StudioBillingInterval =>
  interval === 'annual' ? 'year' : 'month';

/**
 * Whether a plan card under the given toggle tab IS the current subscription:
 * plan and billing interval must both match, so an annual subscriber browsing
 * the monthly tab sees a regular change target, not their current plan. The
 * credit total is a variant picker inside the card, not part of its identity.
 */
export const isCurrentPlanSelection = (
  planId: StudioPlanId,
  interval: StudioBillingInterval,
  currentPlan?: CurrentPlanResponse,
): boolean =>
  isResolvedStudioPlan(currentPlan) &&
  currentPlan.plan === planId &&
  fromApiInterval(currentPlan.interval) === interval;

/**
 * Whether the current plan's card CTA has anything to submit: only a credit
 * total strictly above the subscription's current total is an increase.
 */
export const isCreditIncrease = (
  selectedCredits: number,
  currentPlan?: CurrentPlanResponse,
): boolean => getCreditChangeKind(selectedCredits, currentPlan) === 'increase';

export type CreditChangeKind = 'increase' | 'decrease';

/**
 * How a selected total relates to the subscribed one: an increase applies
 * immediately, a decrease is give-less and schedules at renewal, and equal
 * (or an unresolved plan) is nothing to submit.
 */
export const getCreditChangeKind = (
  selectedCredits: number,
  currentPlan?: CurrentPlanResponse,
): CreditChangeKind | undefined => {
  if (!isCreditManageablePlan(currentPlan)) {
    return undefined;
  }
  if (selectedCredits > currentPlan.credits) {
    return 'increase';
  }
  return selectedCredits < currentPlan.credits ? 'decrease' : undefined;
};

const getPlanAtRank = (rank: number): StudioPlanId | undefined =>
  (Object.keys(STUDIO_PLAN_RANK) as StudioPlanId[]).find(
    (candidate) => STUDIO_PLAN_RANK[candidate] === rank,
  );

/** The plan one tier above, or undefined at the top of the ladder. */
export const getPlanOneTierAbove = (
  plan: StudioPlanId,
): StudioPlanId | undefined => getPlanAtRank(STUDIO_PLAN_RANK[plan] + 1);

/**
 * Whether Free is a give-less target at all: a workspace already on Free has
 * nothing below it, so the manage-plan modal offers no lower plan.
 */
export const isFreeDowngradeAvailable = (
  currentPlan?: CurrentPlanResponse,
): boolean =>
  isResolvedStudioPlan(currentPlan) &&
  STUDIO_PLAN_RANK[currentPlan.plan] > STUDIO_PLAN_RANK.freemium;

/**
 * Builds a change target for a catalog tier at a chosen interval, taking the
 * tier's credits as the target plan's included allowance (the modals offer
 * extras on top of it).
 */
export const toPlanChangeSelection = (
  targetPlan: StudioPlanId,
  interval: StudioBillingInterval,
  tier: StudioPlanTier,
): StudioPlanSelection => ({
  planId: targetPlan,
  planTitle: getStudioPlanTitle(targetPlan),
  interval,
  tier: {
    credits: tier.credits,
    monthlyCents: tier.monthlyCents,
    annualCents: tier.annualCents,
    includedCredits: tier.credits,
  },
});

/**
 * Builds a change target for a catalog plan at its included allowance:
 * freemium is only sold monthly, other targets keep the current billing
 * interval. Undefined when the catalog doesn't carry the plan.
 */
export const buildPlanChangeSelection = (
  targetPlan: StudioPlanId,
  currentPlan: CurrentPlanResponse | undefined,
  plans: StudioPlan[] | undefined,
): StudioPlanSelection | undefined => {
  const tier = plans?.find((plan) => plan.plan === targetPlan)?.tiers.at(0);
  if (!tier) {
    return undefined;
  }
  return toPlanChangeSelection(
    targetPlan,
    targetPlan === 'freemium'
      ? 'month'
      : fromApiInterval(currentPlan?.interval),
    tier,
  );
};

/**
 * The credit totals a plan card offers: the plan's included allowance plus
 * every ladder step above it. `ladder` is the backend's ladder off the plans
 * catalog (`useCreditLadder`) — the same bound the plan-change endpoints
 * validate against, so a card can never offer a quantity the API rejects.
 */
export const getCreditQuantityOptions = (
  includedCredits: number,
  ladder: number[],
): number[] => [
  includedCredits,
  ...ladder.filter((credits) => credits > includedCredits),
];

/**
 * The selector options for a plan: the ladder plus any caller-known totals
 * (subscribed or pre-selected) injected in order when API-side changes put
 * them off-ladder, so the current selection can always render.
 */
export const getCreditSelectorOptions = (
  includedCredits: number,
  ladder: number[],
  ...knownTotals: (number | undefined)[]
): number[] => {
  const options = getCreditQuantityOptions(includedCredits, ladder);
  const missing = new Set(
    knownTotals.filter(
      (total): total is number =>
        total !== undefined && !options.includes(total),
    ),
  );
  if (missing.size === 0) {
    return options;
  }
  return [...options, ...missing].sort((a, b) => a - b);
};

/**
 * PRD extra-credit rate: $0.60 per credit per month. Copy/derivation only —
 * the authoritative charge always comes from the preview endpoint.
 */
export const EXTRA_CREDIT_MONTHLY_CENTS_PER_CREDIT = 60;

/** The recurring monthly total after adding extras at the PRD rate. */
export const getMonthlyTotalWithExtrasCents = (
  baseMonthlyCents: number,
  additionalCredits: number,
): number =>
  baseMonthlyCents + additionalCredits * EXTRA_CREDIT_MONTHLY_CENTS_PER_CREDIT;

/** The upgrade modal's due-today preview request for a selection. */
export const toPlanChangePreviewRequest = (
  selection: StudioPlanSelection,
  totalCredits: number,
): StudioPlanChangePreviewRequest => ({
  plan: selection.planId,
  interval: toApiInterval(selection.interval),
  credits: totalCredits,
});

export type PlanSelectionKind = 'upgrade' | 'downgrade';

/**
 * A current plan the change flows can classify against: a resolved Studio
 * subscription. Anything else (still loading, Classic, none) has no
 * self-serve change flow in this page, so the CTAs gate on this.
 */
export const isResolvedStudioPlan = (
  currentPlan?: CurrentPlanResponse,
): currentPlan is CurrentPlanResponse & { plan: StudioPlanId } =>
  currentPlan?.family === 'studio' && currentPlan.plan !== undefined;

/**
 * A current plan whose CREDIT total can be changed self-serve. Wider than
 * {@link isResolvedStudioPlan}: the backend's credit-change audience is Studio
 * plus self-serve Classic (the platform plans), both of which carry an extras
 * quantity item, so a paid Classic workspace buys credits the same way. Plan
 * changes stay Studio-only — Classic changes plan through its own grid.
 * Freemium is excluded: it sells no extra credits and has to upgrade instead.
 */
export const isCreditManageablePlan = (
  currentPlan?: CurrentPlanResponse,
): currentPlan is CurrentPlanResponse & { credits: number } =>
  (currentPlan?.family === 'studio' || currentPlan?.family === 'classic') &&
  currentPlan.plan !== 'freemium' &&
  currentPlan.credits !== undefined;

/**
 * Whether the resolved subscription bills annually. The two families spell the
 * interval differently: Studio carries the catalog's `annual`, Classic the raw
 * Stripe `year`.
 */
export const isAnnualStudioSubscription = (
  currentPlan?: CurrentPlanResponse,
): boolean =>
  currentPlan?.family === 'studio'
    ? currentPlan.interval === 'annual'
    : currentPlan?.family === 'classic' && currentPlan.interval === 'year';

/** Interval axis rank, mirroring the backend classifier: monthly < annual. */
const STUDIO_INTERVAL_RANK: Record<StudioPlanChangeInterval, number> = {
  monthly: 0,
  annual: 1,
};

// The current plan's interval is a plain string; anything other than the
// annual marker ranks as monthly.
const intervalRank = (interval?: string): number =>
  interval === 'annual'
    ? STUDIO_INTERVAL_RANK.annual
    : STUDIO_INTERVAL_RANK.monthly;

/**
 * Classifies a plan-card choice against the current plan, or undefined when
 * the current plan is not a resolved Studio subscription (no blind requests)
 * or the selection changes nothing.
 *
 * This mirrors backend plans.Classify exactly. A billing-interval DECREASE
 * (annual→monthly) is give-less and always routes to the downgrade chain,
 * whatever the plan or credits — you can't immediately un-commit from an
 * annual term (ScheduleDowngrade accepts it). Otherwise plan rank dominates
 * the credit axis: a higher-ranked plan is an UPGRADE and a lower-ranked plan
 * a DOWNGRADE even when credits move the other way, so a plan-tier upgrade
 * that carries fewer credits still upgrades (its negative credit delta applies
 * immediately). Only WITHIN the same plan (interval non-decreasing) do the
 * remaining axes decide: every axis >= with one strictly greater is an
 * upgrade, anything else — including mixed directions — is a downgrade, and all
 * equal is NO CHANGE. Every upgrade opens the upgrade modal, target-annual
 * included: a monthly→annual switch applies immediately like any other.
 */
export const classifyPlanSelection = (
  selection: StudioPlanSelection,
  currentPlan?: CurrentPlanResponse,
): PlanSelectionKind | undefined => {
  if (!isResolvedStudioPlan(currentPlan)) {
    return undefined;
  }
  const targetInterval = toApiInterval(selection.interval);
  // An interval decrease is give-less and defers as a downgrade regardless of
  // plan or credits; plan rank only dominates when the interval is not dropping.
  if (intervalRank(targetInterval) - intervalRank(currentPlan.interval) < 0) {
    return 'downgrade';
  }
  // Plan rank dominates the credit axis whenever it changes.
  const planDelta =
    STUDIO_PLAN_RANK[selection.planId] - STUDIO_PLAN_RANK[currentPlan.plan];
  if (planDelta < 0) {
    return 'downgrade';
  }
  if (planDelta > 0) {
    return 'upgrade';
  }
  // Same plan, interval non-decreasing: order by the remaining axes.
  const remainingDeltas = [
    // An unknown current credit total compares as equal on that axis.
    currentPlan.credits !== undefined
      ? selection.tier.credits - currentPlan.credits
      : 0,
    intervalRank(targetInterval) - intervalRank(currentPlan.interval),
  ];
  const someAxisDown = remainingDeltas.some((delta) => delta < 0);
  const someAxisUp = remainingDeltas.some((delta) => delta > 0);
  if (someAxisDown) {
    return 'downgrade';
  }
  if (!someAxisUp) {
    return undefined;
  }
  return 'upgrade';
};

/** The CTA a grid plan card renders, derived from its classified change. */
export type StudioPlanCardCta =
  | 'subscribe'
  | 'upgrade'
  | 'downgrade'
  | 'current'
  | 'upgradeCurrent'
  | 'downgradeCurrent';

/**
 * The grid plan-card CTA for a selection. The current-plan card submits a
 * credits-only change (increase → upgradeCurrent, decrease → downgradeCurrent);
 * with no credit change it has nothing to submit, so its disabled CTA reads
 * as the current plan rather than an upgrade. Every other card's label follows
 * the same classification that routes its click, so an annual→monthly
 * (give-less) selection reads "downgrade" and matches the survey it opens.
 *
 * Freemium is the exception: it pays nothing today, so every paid card is a
 * first subscription rather than an upgrade from one.
 */
export const getStudioPlanCardCta = (
  selection: StudioPlanSelection,
  currentPlan: CurrentPlanResponse | undefined,
  isCurrentPlan: boolean,
  creditChangeKind: CreditChangeKind | undefined,
): StudioPlanCardCta => {
  if (isCurrentPlan) {
    if (creditChangeKind === undefined) {
      return 'current';
    }
    return creditChangeKind === 'decrease'
      ? 'downgradeCurrent'
      : 'upgradeCurrent';
  }
  if (classifyPlanSelection(selection, currentPlan) === 'downgrade') {
    return 'downgrade';
  }
  return isResolvedStudioPlan(currentPlan) && currentPlan.plan === 'freemium'
    ? 'subscribe'
    : 'upgrade';
};

/**
 * What a pending change is really about, in the order that reads best: a new
 * plan names the plan, otherwise a new billing interval names the interval,
 * otherwise it's credits-only — naming an unchanged plan or interval would
 * read as a no-op.
 */
export const getPendingChangeTitle = (
  pendingChange: PendingPlanChange,
  currentPlanId?: StudioPlanId,
  currentPlanInterval?: string,
): string => {
  const date = formatStudioPlanDate(pendingChange.effectiveAt);
  // Classic schedules credit decreases only — its plan and interval changes
  // apply immediately — so it always reads as a credit change, and never
  // against a Studio plan id it has no equivalent for.
  if (pendingChange.plan === 'classic') {
    return t(($) => $.page_studio_plans.plan_change.pending_title_credits, {
      count: pendingChange.credits,
      date,
    });
  }
  if (pendingChange.plan !== currentPlanId) {
    return t(($) => $.page_studio_plans.plan_change.pending_title, {
      plan: getStudioPlanTitle(pendingChange.plan),
      date,
    });
  }
  if (pendingChange.interval !== currentPlanInterval) {
    return pendingChange.interval === 'annual'
      ? t(
          ($) => $.page_studio_plans.plan_change.pending_title_interval_annual,
          {
            date,
          },
        )
      : t(
          ($) => $.page_studio_plans.plan_change.pending_title_interval_monthly,
          { date },
        );
  }
  return t(($) => $.page_studio_plans.plan_change.pending_title_credits, {
    count: pendingChange.credits,
    date,
  });
};

/**
 * Renders a billing instant (renewal date, downgrade effective date) as a
 * long date. Billing instants are midnight-UTC anchors, so they render in UTC
 * lest a UTC-negative browser show the previous calendar day.
 */
export const formatStudioPlanDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return EMPTY_VALUE;
  }
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export interface StudioFeatureItem {
  label: string;
  badge?: string;
  /**
   * Longer explanation behind a hoverable info icon. The Studio plans state
   * their limits in the label itself; the Classic catalog leans on tooltips.
   */
  tooltip?: string;
}

export interface StudioFeatureGroup {
  title: string;
  items: StudioFeatureItem[];
}

export interface StudioPlanDisplay {
  id: SellableStudioPlanId;
  title: string;
  subTitle: string;
  featureGroups: StudioFeatureGroup[];
}

// PRD included allowance for every paid Studio plan — the i18n fallback when
// the catalog hasn't loaded; the API value wins so enterprise per-portal
// overrides display correctly.
const PRD_INCLUDED_CREDITS = 200;

/**
 * Renders the plan-card bullet lists. `plans` (the /studio-plans catalog)
 * supplies each plan's included credits; absent entries fall back to the PRD
 * number.
 */
export function getStudioPlanDisplays(
  plans?: StudioPlan[],
): StudioPlanDisplay[] {
  const includedCredits = (planId: SellableStudioPlanId): number =>
    plans?.find((plan) => plan.plan === planId)?.tiers.at(0)?.credits ??
    PRD_INCLUDED_CREDITS;

  return [
    {
      id: 'starter',
      title: t(($) => $.page_studio_plans.tiers.starter.title),
      subTitle: t(($) => $.page_studio_plans.tiers.starter.subTitle),
      featureGroups: [
        {
          title: t(($) => $.page_studio_plans.sections.fundamentals),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.active_contacts.starter),
            },
            {
              label: t(($) => $.page_studio_plans.feat.internal_users.starter),
            },
            {
              label: t(($) => $.page_studio_plans.feat.build_credits, {
                count: includedCredits('starter'),
              }),
            },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.everything_in_free_plus),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.api_mcp_connector),
            },
            {
              label: t(($) => $.page_studio_plans.feat.addon_build_credits),
            },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.support),
          items: [{ label: t(($) => $.page_studio_plans.feat.email_support) }],
        },
      ],
    },
    {
      id: 'professional',
      title: t(($) => $.page_studio_plans.tiers.pro.title),
      subTitle: t(($) => $.page_studio_plans.tiers.pro.subTitle),
      featureGroups: [
        {
          title: t(($) => $.page_studio_plans.sections.fundamentals),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.active_contacts.pro),
            },
            {
              label: t(($) => $.page_studio_plans.feat.internal_users.pro),
              tooltip: t(($) => $.page_studio_plans.feat.per_user_tooltip.pro),
            },
            {
              label: t(($) => $.page_studio_plans.feat.build_credits, {
                count: includedCredits('professional'),
              }),
            },
          ],
        },
        {
          title: t(
            ($) => $.page_studio_plans.sections.everything_in_starter_plus,
          ),
          items: [
            { label: t(($) => $.page_studio_plans.feat.custom_domain) },
            {
              label: t(($) => $.page_studio_plans.feat.custom_email_domain),
            },
            { label: t(($) => $.page_studio_plans.feat.remove_badge) },
            { label: t(($) => $.page_studio_plans.feat.app_visibility) },
            {
              label: t(($) => $.page_studio_plans.feat.automation_builder),
            },
            {
              label: t(($) => $.page_studio_plans.feat.ai_model_selection),
            },
            { label: t(($) => $.page_studio_plans.feat.multi_company_clients) },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.support),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.priority_email_support),
            },
          ],
        },
      ],
    },
    {
      id: 'advanced',
      title: t(($) => $.page_studio_plans.tiers.advanced.title),
      subTitle: t(($) => $.page_studio_plans.tiers.advanced.subTitle),
      featureGroups: [
        {
          title: t(($) => $.page_studio_plans.sections.fundamentals),
          items: [
            {
              label: t(
                ($) => $.page_studio_plans.feat.active_contacts.advanced,
              ),
            },
            {
              label: t(($) => $.page_studio_plans.feat.internal_users.advanced),
              tooltip: t(
                ($) => $.page_studio_plans.feat.per_user_tooltip.advanced,
              ),
            },
            {
              label: t(($) => $.page_studio_plans.feat.build_credits, {
                count: includedCredits('advanced'),
              }),
            },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.everything_in_pro_plus),
          items: [
            { label: t(($) => $.page_studio_plans.feat.audit_log) },
            {
              label: t(
                ($) => $.page_studio_plans.feat.client_access_permissions,
              ),
            },
            { label: t(($) => $.page_studio_plans.feat.enforced_mfa) },
            {
              label: t(($) => $.page_studio_plans.feat.hipaa_compliance),
            },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.support),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.priority_email_support),
            },
            {
              label: t(($) => $.page_studio_plans.feat.priority_call_support),
            },
            {
              label: t(($) => $.page_studio_plans.feat.personalized_onboarding),
            },
          ],
        },
      ],
    },
  ];
}

/**
 * Formats a cents amount as a dollar string, dropping the decimals when the
 * amount is a whole dollar figure (e.g. 5900 -> "$59", 374250 -> "$3,742.50").
 */
export function formatDollars(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}
